import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { Card, Tooltip } from '@mui/material';
import { Ball, getClassificationText } from '../../coloredBall/index';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../../i18n/useAppLocale';
import { getMonthName } from '../../../i18n/analytics';

interface HistoricData {
  month: string;
  year: number;
  result: number | null;
}

interface TableComponentProps {
  ifozHistoric: HistoricData[];
  ipaHistoric: HistoricData[];
  iepHistoric: HistoricData[];
  ifczHistoric: HistoricData[];
  hasFilter?: boolean;
  companyCreatedAt?: string | null;
}

type YearMonth = {
  year: number;
  month: number;
};

type IndicatorKey = 'ifoz' | 'iap' | 'iep' | 'ifcz';

type MonthContext = YearMonth & {
  key: string;
};

const getMonthIndexFromName = (monthName: string) => {
  const normalizeText = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const monthCurt: Record<string, number> = {
    janeiro: 1,
    fevereiro: 2,
    marco: 3,
    abril: 4,
    maio: 5,
    junho: 6,
    julho: 7,
    agosto: 8,
    setembro: 9,
    outubro: 10,
    novembro: 11,
    dezembro: 12
  };

  return monthCurt[normalizeText(monthName)] ?? 0;
};

const parseCreatedAtToYearMonth = (value?: string | null): YearMonth | null => {
  if (!value) {
    return null;
  }

  const isoMatch = /^(\d{4})-(\d{2})/.exec(value);
  if (isoMatch) {
    return {
      year: Number(isoMatch[1]),
      month: Number(isoMatch[2])
    };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1
  };
};

const isBeforeMonth = (left: YearMonth, right: YearMonth) =>
  left.year < right.year || (left.year === right.year && left.month < right.month);

const isAfterMonth = (left: YearMonth, right: YearMonth) =>
  left.year > right.year || (left.year === right.year && left.month > right.month);

const buildMonthKey = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}`;

const buildMonthContext = (monthItem?: HistoricData | undefined): MonthContext | null => {
  if (!monthItem) {
    return null;
  }

  const monthIndex = getMonthIndexFromName(monthItem.month);
  if (!monthIndex) {
    return null;
  }

  return {
    year: monthItem.year,
    month: monthIndex,
    key: buildMonthKey(monthItem.year, monthIndex)
  };
};

const buildHistoricMap = (items: HistoricData[]) => {
  const historicMap = new Map<string, number | null>();

  items.forEach((item) => {
    const monthContext = buildMonthContext(item);
    if (!monthContext) {
      return;
    }

    historicMap.set(monthContext.key, item.result);
  });

  return historicMap;
};

const isValidNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

const isPositiveNumber = (value: number | null | undefined): value is number =>
  isValidNumber(value) && value > 0;

const getCurrentSystemYearMonth = (): YearMonth => {
  const referenceDate = new Date();
  const currentMonth = referenceDate.getMonth() + 1;

  if (referenceDate.getDate() < 20) {
    if (currentMonth === 1) {
      return {
        year: referenceDate.getFullYear() - 1,
        month: 12
      };
    }

    return {
      year: referenceDate.getFullYear(),
      month: currentMonth - 1
    };
  }

  return {
    year: referenceDate.getFullYear(),
    month: currentMonth
  };
};

const getNullValueTooltipText = (indicatorKey: IndicatorKey, t: (key: string) => string): string => {
  if (indicatorKey === 'iap') return t('shared.noReflection');
  if (indicatorKey === 'iep' || indicatorKey === 'ifcz') return t('shared.threeMonths');
  return t('shared.minRespondents');
};

const getIndicatorLabel = (indicatorKey: IndicatorKey, t: (key: string) => string, hasFilter?: boolean) => {
  const suffix = hasFilter ? ` (${t('shared.filterSuffix')})` : '';
  const labels: Record<IndicatorKey, string> = {
    ifoz: t('indicator.ifoz'), iap: t('indicator.iap'), iep: t('indicator.iep'), ifcz: t('indicator.ifcz')
  };
  return `${labels[indicatorKey]}${suffix}`;
};

const getMonthLabel = (monthItem: HistoricData, locale: Parameters<typeof getMonthName>[1]) => {
  const monthIndex = getMonthIndexFromName(monthItem.month);
  return `${getMonthName(monthIndex, locale, true)}/${String(monthItem.year).slice(-2)}`;
};

const formatPercentageValue = (value: number, locale: Parameters<typeof getMonthName>[1]) =>
  (() => {
    const roundedValue = Math.round(value * 10) / 10;
    return Number.isInteger(roundedValue)
      ? roundedValue.toLocaleString(locale, { maximumFractionDigits: 0 })
      : roundedValue.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  })();

const TableComponent: React.FC<TableComponentProps> = ({ ifozHistoric, ipaHistoric, iepHistoric, ifczHistoric, hasFilter, companyCreatedAt }) => {
  const { t } = useTranslation('insights');
  const appLocale = useAppLocale();
  const classificationLabels = { critical: t('classificationCritical'), bad: t('classificationBad'), fair: t('classificationFair'), good: t('classificationGood'), excellent: t('classificationExcellent') };
  const displayedMonths = ifozHistoric.slice().reverse();
  const currentYearMonth = getCurrentSystemYearMonth();
  const companyCreatedYearMonth = parseCreatedAtToYearMonth(companyCreatedAt);

  const historicMaps = {
    ifoz: buildHistoricMap(ifozHistoric),
    iap: buildHistoricMap(ipaHistoric),
    iep: buildHistoricMap(iepHistoric),
    ifcz: buildHistoricMap(ifczHistoric)
  };

  const resolveCellState = (indicatorKey: IndicatorKey, monthItem: HistoricData | undefined) => {
    const monthContext = buildMonthContext(monthItem);
    if (!monthContext) {
      return {
        value: null,
        visible: false,
        tooltip: getNullValueTooltipText(indicatorKey, t)
      };
    }

    const ifozValue = historicMaps.ifoz.get(monthContext.key) ?? null;
    const iapValue = historicMaps.iap.get(monthContext.key) ?? null;
    const hasValidIfoz = isValidNumber(ifozValue);
    const hasPositiveIap = isPositiveNumber(iapValue);
    const hasReleaseForMonth = hasValidIfoz || hasPositiveIap;
    const isFutureMonth = isAfterMonth(monthContext, currentYearMonth);
    const shouldBypassDateRestrictions = hasValidIfoz;

    if (companyCreatedYearMonth && isBeforeMonth(monthContext, companyCreatedYearMonth) && !shouldBypassDateRestrictions) {
      return {
        value: null,
        visible: false,
        tooltip: getNullValueTooltipText(indicatorKey, t)
      };
    }

    if (isFutureMonth && !hasReleaseForMonth && !shouldBypassDateRestrictions) {
      return {
        value: null,
        visible: false,
        tooltip: getNullValueTooltipText(indicatorKey, t)
      };
    }

    const shouldDisplay =
      indicatorKey === 'ifoz'
        ? hasValidIfoz
        : hasReleaseForMonth || shouldBypassDateRestrictions;

    if (!shouldDisplay) {
      return {
        value: null,
        visible: false,
        tooltip: getNullValueTooltipText(indicatorKey, t)
      };
    }

    const value = historicMaps[indicatorKey].get(monthContext.key) ?? null;

    return {
      value,
      visible: true,
      tooltip: value !== null ? getClassificationText(value, classificationLabels) : getNullValueTooltipText(indicatorKey, t)
    };
  };

  const columns = displayedMonths.map((item) => getMonthLabel(item, appLocale));
  const rows: Array<{ key: IndicatorKey; label: string }> = [
    { key: 'ifoz', label: getIndicatorLabel('ifoz', t, hasFilter) },
    { key: 'iap', label: getIndicatorLabel('iap', t, hasFilter) },
    { key: 'iep', label: getIndicatorLabel('iep', t, hasFilter) },
    { key: 'ifcz', label: getIndicatorLabel('ifcz', t, hasFilter) }
  ];

  return (
    <TableContainer component={Card} sx={{ boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.08)', borderRadius: '.7em' }}>
      <Table sx={{ padding: '1em', fontSize: '14px' }} aria-label="simple table">
        <TableHead sx={{ padding: '.5em' }}>
          <TableRow>
            <TableCell style={{ borderRight: '2px solid #F1F1F1' }} />
            {columns.map((month, index) => (
              <TableCell
                sx={{
                  fontSize: '18px',
                  '@media (max-width:601px)': {
                    fontSize: '14px'
                  },
                  '@media (min-width: 602px) and (max-width:1440px)': {
                    fontSize: '14px'
                  }
                }}
                key={index}
                align="center"
              >
                {month}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell
                sx={{
                  width: '5%',
                  fontSize: '18px',
                  '@media (max-width:601px)': {
                    fontSize: '14px'
                  },
                  '@media (min-width: 602px) and (max-width:1440px)': {
                    fontSize: '14px'
                  }
                }}
                style={{ borderRight: '2px solid #F1F1F1' }}
                component="th"
                scope="row"
              >
                {row.label}
              </TableCell>

              {displayedMonths.map((monthItem, index) => {
                const cellState = resolveCellState(row.key, monthItem);

                return (
                  <TableCell
                    sx={{
                      padding: '1em',
                      fontSize: '18px',
                      '@media (max-width:601px)': {
                        fontSize: '14px'
                      },
                      '@media (min-width: 602px) and (max-width:1440px)': {
                        fontSize: '14px',
                        padding: '0'
                      }
                    }}
                    key={`${row.key}-${index}`}
                    align="center"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.4em', color: '#878B8E' }}>
                      <Tooltip
                        title={cellState.tooltip}
                        placement="top"
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={2000}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, -8]
                                }
                              }
                            ]
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.4em', color: '#878B8E', minWidth: '61.73px' }}>
                          {cellState.value === null ? '- -' : `${formatPercentageValue(cellState.value, appLocale)}%`}
                          <Ball percentage={cellState.visible ? cellState.value : null} />
                        </div>
                      </Tooltip>
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableComponent;
